<template>
  <div class="panel-content-wrapper">
    <p v-if="props.scope.kind === ScopeRefKind.WORKSPACE" class="title">{{ $t('app.panels.addMember.admin.title') }}</p>
    <p v-else-if="props.scope.kind === ScopeRefKind.CAMPAIGN" class="title">{{ $t('app.panels.addMember.consultant.title') }}</p>
    <p v-else class="title">{{ $t('app.panels.addMember.organisation.title') }}</p>
    <MemberEmailSelect input-class="m" v-model="selectedEmail" :members="memberDetails" @change="emailChanged"></MemberEmailSelect>
    <div v-if="!selectedMember" class="inline">
      <VField class="m" name="firstName" v-model="state.firstName" :label="$t('other.form.firstName')" placeholder="Vanessa"></VField>
      <VField class="m" name="lastName" v-model="state.lastName" :label="$t('other.form.lastName')" placeholder="Denfer"></VField>
    </div>
    <!-- <VField class="m" name="email" v-model="state.email" :label="$t('other.form.email')" placeholder="vanessa.denfer@gmail.com"></VField> -->
    <div class="edit-buttons">
      <Button class="m green" :disabled="!isValid" :label="$t('other.actions.confirm')" @click="confirm"></Button>
      <Button class="m secondary" :label="$t('other.actions.cancel')" @click="emits('close')"></Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'vue-toastification';

import VField from '@/components/global/VField.vue';
import Button from '@/components/global/Button.vue';
import MemberEmailSelect from '../input/MemberEmailSelect.vue';

import { DefaultAppRole } from '@/types/role';
import { MemberDetails, ScopeRef, ScopeRefKind } from '@/types/member';

import { useInviteInOrganisation } from '@/queries/invitation.query';
import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { useCreateMemberGrants, useMembersDetails } from '@/queries/members.query';

const emits = defineEmits(['close']);

const { t } = useI18n();
const toast = useToast();

const { data: allRoles } = useAllOrgnisationRoles();
const { data: memberDetails } = useMembersDetails();
const { mutate: inviteInOrganisation } = useInviteInOrganisation();
const { mutate: createMemberGrants } = useCreateMemberGrants();

const selectedEmail = ref("");
const selectedMember: Ref<MemberDetails | undefined> = ref(undefined);

const props = defineProps<{
  scope: ScopeRef,
}>();

const state = reactive({
  firstName: "",
  lastName: "",
});

const targetRoleId = computed(() => {
  const roleName = props.scope.kind === ScopeRefKind.WORKSPACE ? DefaultAppRole.ADMINISTRATOR : DefaultAppRole.CONSULTANT;
  return allRoles?.value?.find((role) => role?.name === roleName)?.id;
});

const isValid = computed(() => {
  if (!!selectedMember.value?.member) return true;
  else if (selectedEmail.value?.length && state.firstName?.length && state.lastName?.length) return true;
  return false;
})

function emailChanged(p: { email: string, member?: MemberDetails}) {
  selectedMember.value = p?.member;
}

function confirm() {
  if (!!selectedMember.value) {
    addGrantToMember();
  } else {
    inviteMember();
  }
}

function addGrantToMember() {
  createMemberGrants({
    memberId: selectedMember.value.member.id,
    roleId: targetRoleId.value,
    scopes: [props.scope],
  }, {
    onSettled: () => {
      selectedMember.value = undefined;
    },
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
    },
    onError: (error) => {
      console.error(error);
      toast.error(t('other.error.ERROR_UNKNOWN'));
    }
  });
}

function inviteMember() {
  inviteInOrganisation({
    email: selectedEmail.value,
    firstName: state.firstName,
    lastName: state.lastName,
    roleId: targetRoleId.value,
    scopes: [props.scope],
    sendEmail: true,
  }, {
    onSettled: () => {
      selectedEmail.value = "",
      state.firstName = "",
      state.lastName = "";
    },
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
    },
    onError: (error) => {
      console.error(error);
      toast.error(t('other.error.ERROR_UNKNOWN'));
    }
  });
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.panel-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 1.5rem;

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .edit-buttons {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 10px;
  }

  .inline {
    display: flex;
    gap: 8px;
    
    .input-wrapper {
      flex: 1;
    }
  }
}
</style>